import * as Sentry from '@sentry/react';
import { Amplify } from 'aws-amplify';
import Cookies from 'js-cookie';
import { createRoot } from 'react-dom/client';

import ErrorFallback from '@/components/ErrorFallback.jsx';

import { AuthContextTwo } from '@/context/DoctorsContextTwo.jsx';
import { AuthContext } from '@/context/ProjectContext.jsx';

import { registerChunkErrorHandler } from '@/utils/chunk-reload-utils.ts';

import { useLoggingStore, usePersistedPrismicStore } from '@/store.js';

import App from './App.tsx';
import awsExports from './aws-exports.js';
import './i18n.js';
import './index.css';
import './sentry.js';
import { isDevEnvironment, isProdEnvironment } from './utils/helpers.js';

if (isProdEnvironment()) {
    console.debug = () => {};
}

if (isDevEnvironment()) {
    console.warn = (...args) => {
        console.error('[Warning as Error]', ...args);
    };
}

const clearCookies = () => {
    const allCookies = Cookies.get(); // Get all cookies
    for (const cookie in allCookies) {
        if (Object.hasOwnProperty.call(allCookies, cookie)) {
            Cookies.remove(cookie); // Clear each cookie
        }
    }
};

const storedAppVersion = localStorage.getItem('appVersion');

if (!storedAppVersion) {
    localStorage.setItem('appVersion', import.meta.env.VITE_VERSION);
    clearCookies();
} else if (storedAppVersion !== import.meta.env.VITE_VERSION) {
    localStorage.clear();
    localStorage.setItem('appVersion', import.meta.env.VITE_VERSION);
    clearCookies();
    window.location.reload();
    throw new Error('Reloading page due to app version change');
}

// Recover from stale code-split chunks after a deploy.
registerChunkErrorHandler();

Amplify.configure(awsExports);

// hydrate prismic store
usePersistedPrismicStore.getState().loadStore();

const logError = (error, info) => {
    console.debug(error);
    console.debug(info);
    console.debug(useLoggingStore.getState().context);
};

const container = document.querySelector('#root');
const root = createRoot(container, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
    <AuthContextTwo>
        <AuthContext>
            <Sentry.ErrorBoundary onError={logError} fallback={ErrorFallback}>
                <App />
            </Sentry.ErrorBoundary>
        </AuthContext>
    </AuthContextTwo>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.debug))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
